/**
 * Perfil — full editable profile.
 *
 * Mirrors the onboarding wizard 1:1. Every field the wizard collects is
 * editable here. Save strategy: debounced per-field via useUpdateBusinessContext.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { AppLayout } from "@/components/layout/AppLayout";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  User,
  ChartLineUp,
  ShareNetwork,
  Sparkle,
  Briefcase,
  ChatCircleText,
  Wrench,
  Compass,
  UsersThree,
  CircleNotch,
  Fingerprint,
} from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useUpdateBusinessContext } from "@/features/onboarding/hooks/useUpdateBusinessContext";
import { useOnboardingNote } from "@/features/onboarding/hooks/useOnboardingNote";
import {
  IdentityFields,
  type IdentityFieldsValues,
} from "@/features/onboarding/components/fields/IdentityFields";
import {
  ProfessionalFields,
  type ProfessionalFieldsValues,
  NICHES,
  REGISTRY_TYPES,
} from "@/features/onboarding/components/fields/ProfessionalFields";
import {
  SocialFields,
  type SocialChannels,
} from "@/features/onboarding/components/fields/SocialFields";
import {
  VoiceFields,
  type VoiceFieldsValues,
} from "@/features/onboarding/components/fields/VoiceFields";
import {
  FunnelFields,
} from "@/features/onboarding/components/fields/FunnelFields";
import { AudienceFields } from "@/features/onboarding/components/fields/AudienceFields";
import {
  PersonalityFields,
  type PersonalityValue,
} from "@/features/onboarding/components/fields/PersonalityFields";
import type { EnneagramTypeId } from "@/types/supabase";
import {
  ProceduresFields,
  centsToDisplay,
} from "@/features/onboarding/components/fields/ProceduresFields";
import { LgpdSection } from "@/features/perfil/components/LgpdSection";

type Niche = (typeof NICHES)[number];
type RegistryType = (typeof REGISTRY_TYPES)[number];

interface PersonaShape {
  summary?: string;
  [k: string]: unknown;
}

const POSITIONING_FIELDS: Array<{
  key:
    | "bio"
    | "unique_mechanism"
    | "differentiator"
    | "pains_solved"
    | "transformation_promise";
  label: string;
  placeholder: string;
}> = [
  { key: "bio", label: "Mini-bio", placeholder: "Quem você é em 1-2 frases." },
  { key: "unique_mechanism", label: "Mecanismo único", placeholder: "Sua metodologia própria." },
  { key: "differentiator", label: "Diferencial", placeholder: "O que te coloca à frente." },
  { key: "pains_solved", label: "Dores que você resolve", placeholder: "Problemas concretos do cliente." },
  { key: "transformation_promise", label: "Promessa", placeholder: "Antes → depois concreto." },
];

interface SectionProps {
  icon: typeof User;
  title: string;
  description: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, description, children }: SectionProps) {
  return (
    <div className="bg-card border border-border/50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon weight="duotone" className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Perfil() {
  usePageTitle("Perfil");
  const { query, completenessPct } = useBusinessContext();
  const { mutate, mutateDebounced, isPending } = useUpdateBusinessContext();
  const { notes, setNotes } = useOnboardingNote();
  const row = query.data;

  const identityDefaults = useMemo<Partial<IdentityFieldsValues> | null>(() => {
    if (!row) return null;
    return {
      honorific_prefix:
        (row.honorific_prefix as IdentityFieldsValues["honorific_prefix"] | null | undefined) ?? "",
      display_name: row.display_name ?? "",
      gender: (row.gender ?? undefined) as IdentityFieldsValues["gender"] | undefined,
      age: row.age ?? undefined,
      photo_url: row.photo_url ?? null,
    };
  }, [row]);

  const professionalDefaults = useMemo<Partial<ProfessionalFieldsValues> | null>(() => {
    if (!row) return null;
    return {
      niche: (NICHES as readonly string[]).includes(row.niche ?? "")
        ? (row.niche as Niche)
        : undefined,
      formations: row.formations ?? [],
      registry_type: (REGISTRY_TYPES as readonly string[]).includes(row.registry_type ?? "")
        ? (row.registry_type as RegistryType)
        : undefined,
      registry_number: row.registry_number ?? "",
      years_active: row.years_active ?? undefined,
      clients_served_estimate: row.clients_served_estimate ?? null,
    };
  }, [row]);

  const [positioning, setPositioning] = useState({
    bio: "",
    unique_mechanism: "",
    differentiator: "",
    pains_solved: "",
    transformation_promise: "",
  });
  const [offers, setOffers] = useState({
    main_offer: "",
    ethical_constraints: "",
  });
  const [social, setSocial] = useState<SocialChannels>({});
  const [voice, setVoice] = useState<VoiceFieldsValues>({
    tone_formality: 5,
    banned_words: [],
    anchor_words: [],
    example_phrases: [],
  });
  const [funnelSources, setFunnelSources] = useState<string[]>([]);
  const [audienceSummary, setAudienceSummary] = useState("");
  const [ticketDisplay, setTicketDisplay] = useState("");
  const [personality, setPersonality] = useState<PersonalityValue>({
    enneagram_type: null,
    enneagram_wing: null,
    enneagram_source: null,
  });
  const seededOnceRef = useRef(false);

  // Seed from server once. Don't resync on subsequent fetches — would clobber
  // mid-edit fields when the query refetches after debounced saves invalidate.
  useEffect(() => {
    if (!row || seededOnceRef.current) return;
    seededOnceRef.current = true;
    setPositioning({
      bio: row.bio ?? "",
      unique_mechanism: row.unique_mechanism ?? "",
      differentiator: row.differentiator ?? "",
      pains_solved: row.pains_solved ?? "",
      transformation_promise: row.transformation_promise ?? "",
    });
    setOffers({
      main_offer: row.main_offer ?? "",
      ethical_constraints: row.ethical_constraints ?? "",
    });
    const ch = (row.social_channels ?? {}) as SocialChannels;
    setSocial({
      instagram: ch.instagram ?? undefined,
      tiktok: ch.tiktok ?? undefined,
      youtube: ch.youtube ?? undefined,
      website: ch.website ?? undefined,
    });
    setVoice({
      tone_formality: row.tone_formality ?? 5,
      banned_words: row.banned_words ?? [],
      anchor_words: row.anchor_words ?? [],
      example_phrases: row.example_phrases ?? [],
    });
    setFunnelSources(((row as Record<string, unknown>).funnel_sources as string[] | null) ?? []);
    const persona = (row.persona ?? {}) as PersonaShape;
    setAudienceSummary(typeof persona.summary === "string" ? persona.summary : "");
    setTicketDisplay(centsToDisplay(row.ticket_medio_cents ?? null));
    setPersonality({
      enneagram_type:
        ((row as Record<string, unknown>).enneagram_type as EnneagramTypeId | null) ?? null,
      enneagram_wing:
        ((row as Record<string, unknown>).enneagram_wing as EnneagramTypeId | null) ?? null,
      enneagram_source:
        ((row as Record<string, unknown>).enneagram_source as PersonalityValue["enneagram_source"]) ?? null,
    });
  }, [row]);

  const handlePositioning = (key: keyof typeof positioning, val: string) => {
    setPositioning((prev) => ({ ...prev, [key]: val }));
    mutateDebounced({ [key]: val });
  };

  const handleOffers = (key: keyof typeof offers, val: string) => {
    setOffers((prev) => ({ ...prev, [key]: val }));
    mutateDebounced({ [key]: val });
  };

  const handleSocial = (next: SocialChannels) => {
    setSocial(next);
    mutateDebounced({ social_channels: next });
  };

  const handleVoice = (patch: Partial<VoiceFieldsValues>) => {
    setVoice((prev) => ({ ...prev, ...patch }));
    mutateDebounced(patch as never);
  };

  const handleFunnelSources = (next: string[]) => {
    setFunnelSources(next);
    mutateDebounced({ funnel_sources: next } as never);
  };

  const handleAudience = (next: string) => {
    setAudienceSummary(next);
    const existing = (row?.persona ?? {}) as PersonaShape;
    mutateDebounced({
      persona: { ...existing, summary: next },
    } as never);
  };

  const handleAudienceGenerated = async (next: string) => {
    // Update local state immediately so the textarea reflects the AI result.
    setAudienceSummary(next);
    const existing = (row?.persona ?? {}) as PersonaShape;
    try {
      await mutate({
        persona: { ...existing, summary: next },
        persona_generated_at: new Date().toISOString(),
        persona_generated_by: "ai",
      } as never);
    } catch {
      toast.error("Erro ao salvar. Tente novamente.");
    }
  };

  const handleTicket = (display: string, cents: number | null) => {
    setTicketDisplay(display);
    mutateDebounced({ ticket_medio_cents: cents } as never);
  };

  const handlePersonality = (next: PersonalityValue) => {
    setPersonality(next);
    mutateDebounced(next);
  };

  const handleMainOffer = (next: string) => {
    setOffers((prev) => ({ ...prev, main_offer: next }));
    mutateDebounced({ main_offer: next || null } as never);
  };

  const handleSaveAll = async () => {
    const existing = (row?.persona ?? {}) as PersonaShape;
    await mutate({
      ...positioning,
      ...offers,
      ...voice,
      social_channels: social,
      funnel_sources: funnelSources,
      persona: { ...existing, summary: audienceSummary },
    } as never);
  };

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in w-full pb-12">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              <span className="accent-text">Perfil</span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Quanto mais completo, melhor a IA imita seu jeito.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-full">
            <span className="text-xs text-muted-foreground">Completude</span>
            <span className="text-sm font-semibold text-primary">
              {completenessPct}%
            </span>
          </div>
        </header>

        {query.isPending ? (
          <div className="space-y-6">
            {[
              { fields: 3 },
              { fields: 4 },
              { fields: 2 },
            ].map((s, i) => (
              <div key={i} className="bg-card border border-border/50 rounded-xl p-6 space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-44" />
                  </div>
                </div>
                <div className="space-y-4">
                  {Array.from({ length: s.fields }).map((_, j) => (
                    <div key={j} className="space-y-1.5">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-9 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <Section icon={User} title="Identidade" description="Como você se apresenta.">
              <IdentityFields
                key={identityDefaults ? "id-loaded" : "id-empty"}
                defaultValues={identityDefaults ?? undefined}
                onChange={(patch) => mutateDebounced(patch)}
              />
            </Section>

            <Section
              icon={ChartLineUp}
              title="Atuação profissional"
              description="Onde você atua e o que te credencia."
            >
              <ProfessionalFields
                key={professionalDefaults ? "pro-loaded" : "pro-empty"}
                defaultValues={professionalDefaults ?? undefined}
                onChange={(patch) => mutateDebounced(patch)}
              />
            </Section>

            <Section
              icon={Sparkle}
              title="Posicionamento"
              description="Mecanismo único, diferencial e promessa."
            >
              <div className="space-y-5">
                {POSITIONING_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`pf-${f.key}`}>{f.label}</Label>
                    <Textarea
                      id={`pf-${f.key}`}
                      value={positioning[f.key]}
                      onChange={(e) => handlePositioning(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                ))}
              </div>
            </Section>

            <Section
              icon={Fingerprint}
              title="Código Interno"
              description="Sua personalidade no Eneagrama. A IA calibra feedback e copy ao seu estilo."
            >
              <PersonalityFields value={personality} onChange={handlePersonality} />
            </Section>

            <Section
              icon={ChatCircleText}
              title="Voz"
              description="Tom, palavras-âncora e frases típicas."
            >
              <VoiceFields value={voice} onChange={handleVoice} />
            </Section>

            <Section
              icon={Wrench}
              title="Procedimentos"
              description="O que você oferece, valores e formato."
            >
              <ProceduresFields
                ticketDisplay={ticketDisplay}
                onTicketChange={handleTicket}
                mainOffer={offers.main_offer}
                onMainOfferChange={handleMainOffer}
              />
            </Section>

            <Section
              icon={Briefcase}
              title="Oferta e ética"
              description="Restrições éticas — o que você não promete."
            >
              <div className="space-y-1.5">
                <Label htmlFor="pf-ethical_constraints">Restrições éticas</Label>
                <Textarea
                  id="pf-ethical_constraints"
                  value={offers.ethical_constraints}
                  onChange={(e) => handleOffers("ethical_constraints", e.target.value)}
                  placeholder="O que você não promete."
                  rows={2}
                  className="resize-none"
                />
              </div>
            </Section>

            <Section
              icon={Compass}
              title="Funil"
              description="Como o cliente chega até você."
            >
              <FunnelFields
                sources={funnelSources}
                onSourcesChange={handleFunnelSources}
                notes={notes}
                onNotesChange={setNotes}
              />
            </Section>

            <Section
              icon={UsersThree}
              title="Público"
              description="Resumo do seu cliente ideal."
            >
              <AudienceFields
                value={audienceSummary}
                onChange={handleAudience}
                onGenerated={handleAudienceGenerated}
              />
            </Section>

            <Section
              icon={ShareNetwork}
              title="Presença online"
              description="Instagram, TikTok, YouTube e site."
            >
              <SocialFields value={social} onChange={handleSocial} />
            </Section>

            <LgpdSection />

            <div className="flex items-center justify-end gap-3">
              <span className="text-xs text-muted-foreground">
                {isPending ? "Salvando…" : "Salvo automaticamente"}
              </span>
              <Button onClick={handleSaveAll} disabled={isPending}>
                {isPending ? (
                  <>
                    <CircleNotch weight="bold" className="mr-2 w-4 h-4 animate-spin" />
                    Salvando
                  </>
                ) : (
                  "Salvar agora"
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
